import React, { useState, useMemo } from 'react';
import Editor from '@monaco-editor/react';
import * as yaml from 'js-yaml';

export const JsonYamlView: React.FC = () => {
  const [input, setInput] = useState<string>('{\n  "name": "MamaJSON",\n  "awesome": true\n}');
  const [mode, setMode] = useState<'json-to-yaml' | 'yaml-to-json'>('json-to-yaml');

  const output = useMemo(() => {
    try {
      if (!input.trim()) {
        return '';
      }

      if (mode === 'json-to-yaml') {
        const parsed = JSON.parse(input) as unknown;
        const yamlStr = yaml.dump(parsed);
        return yamlStr;
      } else {
        const result = yaml.load(input);
        return JSON.stringify(result, null, 2);
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        return `// Error:\n// ${err.message}`;
      }
      return `// Error:\n// ${String(err)}`;
    }
  }, [input, mode]);

  const toggleMode = () => {
    setMode(prev => prev === 'json-to-yaml' ? 'yaml-to-json' : 'json-to-yaml');
    setInput(output);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>JSON ↔ YAML</h2>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--color-text-secondary)' }}>Instantly translate between YAML and JSON formats.</p>
        </div>
        <button onClick={toggleMode} style={{ padding: '0.5rem 1rem', background: 'var(--color-accent)', color: 'var(--color-on-accent)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>
          Switch to {mode === 'json-to-yaml' ? 'YAML → JSON' : 'JSON → YAML'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: '1rem', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ fontSize: '0.9rem', marginBottom: '0.5rem', color: 'var(--color-text-secondary)' }}>
            {mode === 'json-to-yaml' ? 'JSON Input' : 'YAML Input'}
          </h3>
          <div style={{ flex: 1, minHeight: 0, position: 'relative', border: '1px solid var(--color-border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
            <Editor
              height="100%"
              language={mode === 'json-to-yaml' ? 'json' : 'yaml'}
              value={input}
              onChange={(value) => { setInput(value ?? ''); }}
              theme="vs-dark"
              options={{ minimap: { enabled: false }, padding: { top: 16 } }}
            />
          </div>
        </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ fontSize: '0.9rem', marginBottom: '0.5rem', color: 'var(--color-text-secondary)' }}>
            {mode === 'json-to-yaml' ? 'YAML Output' : 'JSON Output'}
          </h3>
          <div style={{ flex: 1, minHeight: 0, position: 'relative', border: '1px solid var(--color-border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
            <Editor
              height="100%"
              language={mode === 'json-to-yaml' ? 'yaml' : 'json'}
              value={output}
              theme="vs-dark"
              options={{ minimap: { enabled: false }, readOnly: true, padding: { top: 16 } }}
            />
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};
